/**
 * Client-side chat encryption using Fernet (compatible with iOS CryptoSwift and backend)
 * Messages are encrypted before sending to server
 * Uses the same key generation as iOS and backend for cross-platform compatibility
 * 
 * Note: iOS uses CryptoSwift library, which implements Fernet specification
 * This implementation uses CryptoJS to be browser-compatible
 */
import CryptoJS from 'crypto-js';

export interface EncryptionKey {
  key: string; // Base64url encoded 32-byte key
  signingKey: CryptoJS.lib.WordArray;
  encryptionKey: CryptoJS.lib.WordArray;
}

// SECRET_KEY must match the one on the backend
// IMPORTANT: In production, this should be obtained from the server or environment variable
const SECRET_KEY = 'super-secret-key-change-in-production-123456789';

/**
 * Generate encryption key for a conversation
 * Uses the same formula as backend and iOS: patientId:effectiveDoctorId:SECRET_KEY
 * For support chat: effectiveDoctorId = patientId (matches iOS).
 * IMPORTANT: For support chat, even if doctorId is provided (admin ID), use patientId:patientId for key.
 */
export function generateConversationKey(conversationId: number, patientId: number, doctorId: number | null | undefined, isSupport?: boolean): EncryptionKey {
  // Support chat (iOS + FERNET_WEB_COMPAT): always patientId:patientId, even when doctor_id is admin.
  // Doctor chat: patientId:doctorId (fallback to patientId if doctor missing).
  const effectiveDoctorId = isSupport ? patientId : (doctorId ?? patientId);
  const keyMaterial = `${patientId}:${effectiveDoctorId}:${SECRET_KEY}`;
  
  // Debug logging (always enabled for troubleshooting)
  if (typeof console !== 'undefined' && console.log) {
    console.log(`[generateConversationKey] conv=${conversationId}, patientId=${patientId}, doctorId=${doctorId}, isSupport=${isSupport}, effectiveDoctorId=${effectiveDoctorId}, keyMaterial=${keyMaterial.substring(0, 50)}...`);
  }
  
  const keyHash = CryptoJS.SHA256(keyMaterial);
  
  // Fernet uses the first 16 bytes for signing key and next 16 bytes for encryption key
  const keyHex = keyHash.toString();
  const signingKeyHex = keyHex.substring(0, 32); // First 16 bytes (32 hex chars)
  const encryptionKeyHex = keyHex.substring(32, 64); // Next 16 bytes (32 hex chars)
  
  const signingKey = CryptoJS.enc.Hex.parse(signingKeyHex);
  const encryptionKey = CryptoJS.enc.Hex.parse(encryptionKeyHex);
  
  // Convert to base64url for storage/comparison
  const keyBytes = CryptoJS.enc.Hex.parse(keyHex);
  const keyBase64 = CryptoJS.enc.Base64.stringify(keyBytes);
  const keyBase64Url = keyBase64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '').substring(0, 44);
  
  return { 
    key: keyBase64Url,
    signingKey: signingKey,
    encryptionKey: encryptionKey
  };
}

/**
 * Extract bytes from WordArray (CryptoJS stores words as 32-bit big-endian).
 * Returns array of bytes [byte0, byte1, ...]
 */
function wordArrayToBytes(wa: CryptoJS.lib.WordArray): number[] {
  const bytes: number[] = [];
  for (let i = 0; i < wa.sigBytes; i++) {
    const wordIndex = i >>> 2;
    const byteIndex = i % 4;
    const word = wa.words[wordIndex];
    const byte = (word >>> (24 - (byteIndex << 3))) & 0xff;
    bytes.push(byte);
  }
  return bytes;
}

/**
 * Create WordArray from byte array.
 * Partial last word must be LEFT-aligned (high bytes), otherwise CryptoJS
 * HMAC/Base64 treat leftover low bytes as zero-pad and Fernet HMAC diverges from iOS/Python.
 */
function bytesToWordArray(bytes: number[]): CryptoJS.lib.WordArray {
  const words: number[] = [];
  for (let i = 0; i < bytes.length; i += 4) {
    let word = 0;
    const n = Math.min(4, bytes.length - i);
    for (let j = 0; j < n; j++) {
      word = (word << 8) | bytes[i + j];
    }
    if (n < 4) {
      word <<= 8 * (4 - n);
    }
    words.push(word >>> 0);
  }
  return CryptoJS.lib.WordArray.create(words, bytes.length);
}

/**
 * Encrypt message in Fernet format (same as iOS) so iOS can decrypt web messages.
 * Fernet: version(1) + timestamp(8) + IV(16) + ciphertext + HMAC(32), then base64url.
 * Structure: byte 0 = version (0x80), bytes 1-8 = timestamp (64-bit big-endian), bytes 9-24 = IV.
 */
export function encryptMessage(message: string, encryptionKey: EncryptionKey): string {
  try {
    // Debug logging (always enabled for troubleshooting)
    if (typeof console !== 'undefined' && console.log) {
      console.log(`[encryptMessage] 🔐 Encrypting message with key: ${encryptionKey.key.substring(0, 20)}... (full key: ${encryptionKey.key})`);
    }
    
    const version = 0x80;
    const t = Math.floor(Date.now() / 1000); // Unix timestamp (32-bit is enough for now, but Fernet uses 64-bit)
    const iv = CryptoJS.lib.WordArray.random(16);

    const encrypted = CryptoJS.AES.encrypt(message, encryptionKey.encryptionKey, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7
    });
    const ciphertext = encrypted.ciphertext;

    // Create timestamp as 8 bytes (64-bit big-endian): high 4 bytes = 0, low 4 bytes = timestamp
    // For 32-bit timestamp: bytes 0-3 = 0, bytes 4-7 = t>>24, t>>16, t>>8, t&0xff
    const timestampBytes = [
      0, 0, 0, 0, // high 32 bits (zero for 32-bit timestamp)
      (t >>> 24) & 0xff,
      (t >>> 16) & 0xff,
      (t >>> 8) & 0xff,
      t & 0xff
    ];
    
    // Build token: version(1) + timestamp(8) + IV(16) + ciphertext + HMAC(32)
    // Use byte arrays only and build final token from bytes to avoid WordArray.concat() mutation issues.
    const versionBytes = [version];
    const payloadBytes = versionBytes.concat(timestampBytes).concat(wordArrayToBytes(iv)).concat(wordArrayToBytes(ciphertext));
    const payloadWA = bytesToWordArray(payloadBytes);
    const hmac = CryptoJS.HmacSHA256(payloadWA, encryptionKey.signingKey);
    const hmacBytes = wordArrayToBytes(hmac);
    const tokenBytes = payloadBytes.concat(hmacBytes);
    const tokenWA = bytesToWordArray(tokenBytes);

    // Convert to base64url (no padding, as iOS expects)
    const base64 = CryptoJS.enc.Base64.stringify(tokenWA);
    const base64url = base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
    // Round-trip check: decrypt with same key to ensure web-generated token is readable
    const roundTrip = tryDecryptFernet(base64url, encryptionKey);
    if (roundTrip !== message) {
      console.error('[encryptMessage] Round-trip FAILED. key=', encryptionKey.key?.substring(0, 12), 'expectedLen=', message?.length, 'got=', roundTrip ?? 'null');
    }
    return base64url;
  } catch (error) {
    console.error('Encryption error:', error);
    throw new Error('Failed to encrypt message');
  }
}

/** Safe UTF-8 decode: returns empty string on Malformed UTF-8 (wrong key/corrupt data) */
function safeUtf8ToString(decrypted: CryptoJS.lib.WordArray): string {
  try {
    return decrypted.toString(CryptoJS.enc.Utf8);
  } catch (_) {
    return '';
  }
}

/** Heuristic to accept AES plaintext when Fernet HMAC is missing/legacy-invalid */
function looksLikePlaintext(text: string): boolean {
  if (!text || text.length > 4000) return false;
  if (/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/.test(text)) return false;
  // Prefer letters/digits/punctuation (incl. Cyrillic); reject empty-looking noise
  return /[A-Za-zА-Яа-яЁё0-9]/.test(text);
}

/**
 * Decode base64url to WordArray (iOS sends without padding).
 * Replace - → +, _ → /; add = padding so length is multiple of 4.
 * IMPORTANT: When input has no padding (e.g. 98 chars), adding padding (98→100 chars)
 * makes decode produce 75 bytes instead of 73. We must trim to actual length: floor(n*3/4).
 */
function decodeBase64url(base64url: string): CryptoJS.lib.WordArray {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const padding = (4 - (base64.length % 4)) % 4;
  const parsed = CryptoJS.enc.Base64.parse(base64 + '='.repeat(padding));
  // Actual decoded length without padding is floor(originalLength * 3 / 4)
  const actualLength = Math.floor((base64url.length * 3) / 4);
  if (parsed.sigBytes > actualLength) {
    // Trim: get bytes, take first actualLength, convert back to WordArray
    const allBytes = wordArrayToBytes(parsed);
    const trimmedBytes = allBytes.slice(0, actualLength);
    return bytesToWordArray(trimmedBytes);
  }
  return parsed;
}

/**
 * Decrypt Fernet token (used by iOS and web).
 * Key: AES = second 16 bytes of SHA256(patientId:doctorId:SECRET_KEY), HMAC = first 16 bytes.
 * Token: version(1) + timestamp(8) + IV(16) + ciphertext + HMAC(32). IV = bytes 9–24, last 32 = HMAC.
 * iOS checks HMAC before decrypting, but decrypts even if HMAC fails (with warning).
 */
function tryDecryptFernet(raw: string, encryptionKey: EncryptionKey): string | null {
  if (raw.length < 50 || raw.includes(':')) return null;
  try {
    // Debug logging (always enabled for troubleshooting)
    if (typeof console !== 'undefined' && console.log) {
      console.log(`[chatEncryption] Trying Fernet decrypt with key: ${encryptionKey.key.substring(0, 20)}..., token length: ${raw.length}, starts with: ${raw.substring(0, 10)}`);
    }
    
    const token = decodeBase64url(raw);
    const tokenBytes = wordArrayToBytes(token);
    if (typeof console !== 'undefined' && console.log) {
      console.log(`[tryDecryptFernet] Decoded token bytes: ${tokenBytes.length} (expected for ${raw.length} chars base64url: ${Math.floor((raw.length * 3) / 4)})`);
    }
    if (tokenBytes.length < 57) return null; // min: 1+8+16+1+32 = 58 bytes
    
    const version = tokenBytes[0];
    if (version !== 0x80) return null;
    
    // Extract parts: version(1) + timestamp(8) + IV(16) + ciphertext + HMAC(32)
    // timestamp = tokenBytes.slice(1, 9); // not used for decryption
    const ivBytes = tokenBytes.slice(9, 25); // bytes 9-24
    const hmacBytes = tokenBytes.slice(tokenBytes.length - 32); // last 32 bytes
    const ciphertextBytes = tokenBytes.slice(25, tokenBytes.length - 32); // bytes 25 to end-32
    
    if (ciphertextBytes.length <= 0 || ciphertextBytes.length > 65536) return null;
    
    // Verify HMAC (like iOS does, but continue even if fails)
    const payloadForHmac = tokenBytes.slice(0, tokenBytes.length - 32); // everything except HMAC
    const payloadForHmacWA = bytesToWordArray(payloadForHmac);
    const computedHmac = CryptoJS.HmacSHA256(payloadForHmacWA, encryptionKey.signingKey);
    const computedHmacBytes = wordArrayToBytes(computedHmac);
    
    // Constant-time comparison (simplified)
    let hmacMatch = true;
    if (computedHmacBytes.length !== hmacBytes.length) hmacMatch = false;
    else {
      for (let i = 0; i < hmacBytes.length; i++) {
        if (computedHmacBytes[i] !== hmacBytes[i]) {
          hmacMatch = false;
          break;
        }
      }
    }
    if (!hmacMatch) {
      // Older web builds had a WordArray alignment bug → invalid HMAC but AES still OK.
      console.warn('[Fernet] HMAC verification failed, continuing AES decrypt for legacy tokens');
    }
    
    // Convert to WordArray for CryptoJS. Pass only ciphertext; iv must be in options (CryptoJS expects this).
    const iv = bytesToWordArray(ivBytes);
    const ciphertext = bytesToWordArray(ciphertextBytes);
    
    // Decrypt with AES key (second 16 bytes) — ciphertext only, iv in options
    let decrypted = CryptoJS.AES.decrypt(
      { ciphertext: ciphertext } as any,
      encryptionKey.encryptionKey,
      { iv: iv, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 }
    );
    let decryptedText = safeUtf8ToString(decrypted);
    // Reject likely false-positives when HMAC failed (random PKCS7 luck on wrong key)
    if (decryptedText && (hmacMatch || looksLikePlaintext(decryptedText))) {
      if (typeof console !== 'undefined' && console.log) {
        console.log(`[chatEncryption] ✅ Successfully decrypted with encryptionKey: "${decryptedText.substring(0, 50)}..."`);
      }
      return decryptedText;
    }
    
    // Fallback: try with signing key (first 16 bytes) in case iOS uses different key
    decrypted = CryptoJS.AES.decrypt(
      { ciphertext: ciphertext } as any,
      encryptionKey.signingKey,
      { iv: iv, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 }
    );
    decryptedText = safeUtf8ToString(decrypted);
    if (decryptedText && (hmacMatch || looksLikePlaintext(decryptedText))) {
      if (typeof console !== 'undefined' && console.log) {
        console.log(`[chatEncryption] ✅ Successfully decrypted with signingKey: "${decryptedText.substring(0, 50)}..."`);
      }
      return decryptedText;
    }
    
    if (typeof console !== 'undefined' && console.log) {
      console.log(`[chatEncryption] ❌ Decryption failed: empty result with both keys`);
    }
    return null;
  } catch (e) {
    console.error('[Fernet] Decrypt error:', e);
    return null;
  }
}

/**
 * Decrypt message. Supports:
 * 1. Fernet (gAAAAA..., no colon) — iOS and web; try FIRST so iOS is never treated as hexIV:base64
 * 2. Simple format: {iv_hex}:{base64_ciphertext} — web only
 * 3. Salted: iv_hex:U2FsdGVkX1... — legacy
 * Key: patientId:effectiveDoctorId:SECRET_KEY, effectiveDoctorId = doctorId ?? patientId (support chat).
 * AES = second 16 bytes of hash (indices 16–31), HMAC = first 16 (0–15).
 * @param encryptedData - encrypted message content
 * @param encryptionKey - primary key (patientId:doctorId)
 * @param encryptionKeySwapped - optional key (doctorId:patientId) to try if iOS uses swapped order
 * @param patientIdForSupport - optional patientId to generate support key (patientId:patientId) if main keys fail
 * @param encryptionKeyLastUsed - optional key last used when sending (try first for web->web own messages)
 * @param doctorIdForKeys - optional doctorId to try doctorId:doctorId key (e.g. when sender was doctor)
 * @param currentUserId - optional current user ID; try patientId:currentUserId so admin (as doctor) can decrypt own messages
 */
export function decryptMessage(encryptedData: string, encryptionKey: EncryptionKey, encryptionKeySwapped?: EncryptionKey | null, patientIdForSupport?: number, encryptionKeyLastUsed?: EncryptionKey | null, doctorIdForKeys?: number | null, currentUserId?: number | null): string {
  try {
    // CRITICAL: Use console.error so logs are always visible (not filtered)
    console.error(`[decryptMessage] START: type=${typeof encryptedData}, length=${encryptedData?.length}, token=${encryptedData?.substring(0, 30)}...`);
    console.error(`[decryptMessage] Keys: primary=${encryptionKey.key}, lastUsed=${encryptionKeyLastUsed ? encryptionKeyLastUsed.key : 'null'}`);
    
    const raw = (encryptedData && typeof encryptedData === 'string') ? encryptedData.trim() : '';
    if (!raw) {
      console.error(`[decryptMessage] Invalid encrypted data: type=${typeof encryptedData}, value=${encryptedData}`);
      return '';
    }

    const colonIdx = raw.indexOf(':');

    const tryFernetWithKeys = (keys: (EncryptionKey | null | undefined)[]): string | null => {
      console.error(`[decryptMessage] 🔄 tryFernetWithKeys: starting with ${keys.length} keys`);
      for (let i = 0; i < keys.length; i++) {
        const k = keys[i];
        if (!k) {
          console.error(`[decryptMessage] ⏭️  Key ${i} is null/undefined, skipping`);
          continue;
        }
        console.error(`[decryptMessage] 🔑 Trying key ${i}: ${k.key.substring(0, 20)}... (full: ${k.key})`);
        const r = tryDecryptFernet(raw, k);
        if (r) {
          console.error(`[decryptMessage] ✅✅✅ Key ${i} SUCCEEDED! Decrypted: "${r.substring(0, 50)}..."`);
          return r;
        }
        console.error(`[decryptMessage] ❌ Key ${i} FAILED`);
      }
      console.error(`[decryptMessage] ❌❌❌ All ${keys.length} keys failed`);
      return null;
    };

    // 1. Fernet first: messages from iOS and web (gAAAAA..., no colon).
    // Try: lastUsed FIRST (for web->web own messages), then keyPatientCurrentUser, support, main, swapped, doctorDoctor.
    if (colonIdx === -1 && (raw.startsWith('gAAAAA') || (raw.startsWith('g') && /^[A-Za-z0-9+/_-]+=*$/.test(raw)))) {
      console.error(`[decryptMessage] 🔍 Fernet token detected, preparing keys...`);
      console.error(`[decryptMessage] Parameters: patientIdForSupport=${patientIdForSupport}, doctorIdForKeys=${doctorIdForKeys}, currentUserId=${currentUserId}`);
      console.error(`[decryptMessage] encryptionKeyLastUsed: ${encryptionKeyLastUsed ? encryptionKeyLastUsed.key : 'null'}`);
      console.error(`[decryptMessage] encryptionKey (main): ${encryptionKey.key}`);
      
      let supportKey: EncryptionKey | null = null;
      let doctorDoctorKey: EncryptionKey | null = null;
      let keyPatientCurrentUser: EncryptionKey | null = null;
      if (patientIdForSupport) {
        supportKey = generateConversationKey(0, patientIdForSupport, patientIdForSupport, true);
        console.error(`[decryptMessage] Generated supportKey: ${supportKey.key}`);
      }
      if (doctorIdForKeys != null && doctorIdForKeys !== patientIdForSupport) {
        doctorDoctorKey = generateConversationKey(0, Number(doctorIdForKeys), Number(doctorIdForKeys));
        console.error(`[decryptMessage] Generated doctorDoctorKey: ${doctorDoctorKey.key}`);
      }
      // Admin (or doctor) viewing support chat: try patientId:currentUserId so our own sent messages decrypt
      if (currentUserId != null && patientIdForSupport != null && currentUserId !== patientIdForSupport) {
        keyPatientCurrentUser = generateConversationKey(0, patientIdForSupport, currentUserId);
        console.error(`[decryptMessage] Generated keyPatientCurrentUser: ${keyPatientCurrentUser.key}`);
      }
      const keysToTry = [encryptionKeyLastUsed, keyPatientCurrentUser, supportKey, encryptionKey, encryptionKeySwapped, doctorDoctorKey];
      console.error(`[decryptMessage] 🔑 Will try ${keysToTry.filter(k => k).length} keys: ${keysToTry.map((k, i) => k ? `key${i}=${k.key.substring(0, 10)}...` : `key${i}=null`).join(', ')}`);
      const fernetResult = tryFernetWithKeys([encryptionKeyLastUsed, keyPatientCurrentUser, supportKey, encryptionKey, encryptionKeySwapped, doctorDoctorKey]);
      if (fernetResult) {
        if (typeof console !== 'undefined' && console.log) {
          console.log(`[decryptMessage] ✅ Successfully decrypted Fernet message`);
        }
        return fernetResult;
      } else {
        if (typeof console !== 'undefined' && console.log) {
          console.log(`[decryptMessage] ❌ Failed to decrypt Fernet message with all key variants`);
        }
      }
    }

    // 2. Formats with colon: hexIV:base64 (simple) or iv_hex:U2FsdGVkX1... (Salted)
    if (colonIdx !== -1) {
      const ivHex = raw.substring(0, colonIdx).trim();
      const secondPart = raw.substring(colonIdx + 1).trim();
      if (ivHex.length === 32 && /^[0-9a-fA-F]+$/.test(ivHex) && secondPart) {
        try {
          const iv = CryptoJS.enc.Hex.parse(ivHex);
          const ciphertext = CryptoJS.enc.Base64.parse(secondPart);
          const decrypted = CryptoJS.AES.decrypt(
            { ciphertext: ciphertext } as CryptoJS.lib.CipherParams,
            encryptionKey.encryptionKey,
            { iv: iv, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 }
          );
          const decryptedText = safeUtf8ToString(decrypted);
          if (decryptedText) return decryptedText;
        } catch (_) {}
      }
      if (secondPart && secondPart.startsWith('U2FsdGVkX1')) {
        try {
          const binaryData = CryptoJS.enc.Base64.parse(secondPart);
          const ciphertextLen = Math.max(0, binaryData.sigBytes - 16);
          const ciphertextOnly = CryptoJS.lib.WordArray.create(binaryData.words.slice(4), ciphertextLen);
          const iv = CryptoJS.enc.Hex.parse(ivHex);
          const decrypted = CryptoJS.AES.decrypt(
            { ciphertext: ciphertextOnly } as any,
            encryptionKey.encryptionKey,
            { iv: iv, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 }
          );
          const decryptedText = safeUtf8ToString(decrypted);
          if (decryptedText) return decryptedText;
          const decryptedAlt = CryptoJS.AES.decrypt(
            { ciphertext: ciphertextOnly } as any,
            encryptionKey.signingKey,
            { iv: iv, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 }
          );
          const altText = safeUtf8ToString(decryptedAlt);
          if (altText) return altText;
        } catch (_) {}
      }
    }

    // 3. Other no-colon formats: raw base64 IV+ciphertext, raw Salted__
    if (colonIdx === -1 && raw.length >= 24 && /^[A-Za-z0-9+/_-]+=*$/.test(raw) && !raw.startsWith('g')) {
      try {
        const binary = decodeBase64url(raw);
        if (binary.sigBytes >= 17) {
          const iv = CryptoJS.lib.WordArray.create(binary.words.slice(0, 4), 16);
          const cipherLen = binary.sigBytes - 16;
          const ciphertext = CryptoJS.lib.WordArray.create(binary.words.slice(4), cipherLen);
          const decrypted = CryptoJS.AES.decrypt(
            { ciphertext: ciphertext } as any,
            encryptionKey.encryptionKey,
            { iv: iv, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 }
          );
          const decryptedText = safeUtf8ToString(decrypted);
          if (decryptedText) return decryptedText;
          const decryptedAlt = CryptoJS.AES.decrypt(
            { ciphertext: ciphertext } as any,
            encryptionKey.signingKey,
            { iv: iv, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 }
          );
          const altText = safeUtf8ToString(decryptedAlt);
          if (altText) return altText;
        }
      } catch (_) {}
    }
    if (colonIdx === -1 && raw.startsWith('U2FsdGVkX1')) {
      try {
        const binaryData = decodeBase64url(raw);
        if (binaryData.sigBytes >= 32) {
          const saltWords = binaryData.words.slice(2, 4);
          const iv = CryptoJS.lib.WordArray.create(saltWords.concat(saltWords), 16);
          const ciphertextLen = binaryData.sigBytes - 16;
          const ciphertextOnly = CryptoJS.lib.WordArray.create(binaryData.words.slice(4), ciphertextLen);
          const decrypted = CryptoJS.AES.decrypt(
            { ciphertext: ciphertextOnly } as any,
            encryptionKey.encryptionKey,
            { iv: iv, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 }
          );
          const decryptedText = safeUtf8ToString(decrypted);
          if (decryptedText) return decryptedText;
          const decryptedAlt = CryptoJS.AES.decrypt(
            { ciphertext: ciphertextOnly } as any,
            encryptionKey.signingKey,
            { iv: iv, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 }
          );
          const altText = safeUtf8ToString(decryptedAlt);
          if (altText) return altText;
        }
      } catch (_) {}
    }

    // 4. Fernet without leading g (e.g. different base64 encoding): try if long base64 and no colon
    if (colonIdx === -1 && raw.length >= 50 && /^[A-Za-z0-9+/_-]+=*$/.test(raw)) {
      let supportKey: EncryptionKey | null = null;
      let doctorDoctorKey: EncryptionKey | null = null;
      let keyPatientCurrentUser: EncryptionKey | null = null;
      if (patientIdForSupport) supportKey = generateConversationKey(0, patientIdForSupport, patientIdForSupport);
      if (doctorIdForKeys != null && doctorIdForKeys !== patientIdForSupport) {
        doctorDoctorKey = generateConversationKey(0, Number(doctorIdForKeys), Number(doctorIdForKeys));
      }
      if (currentUserId != null && patientIdForSupport != null && currentUserId !== patientIdForSupport) {
        keyPatientCurrentUser = generateConversationKey(0, patientIdForSupport, currentUserId);
      }
      const fernetResult = tryFernetWithKeys([encryptionKeyLastUsed, keyPatientCurrentUser, supportKey, encryptionKey, encryptionKeySwapped, doctorDoctorKey]);
      if (fernetResult) return fernetResult;
    }

    // If we get here, all decryption attempts failed — return '' and log (do not throw)
    console.error('[decryptMessage] All decryption attempts failed for:', encryptedData?.substring(0, 50));
    return '';
  } catch (error) {
    console.error('[decryptMessage] Decryption error:', error, 'encryptedData type:', typeof encryptedData, 'length:', encryptedData?.length);
    return '';
  }
}

/**
 * Store encryption key in localStorage (encrypted with user's password hash)
 */
export function storeEncryptionKey(conversationId: number, key: EncryptionKey): void {
  // Store the key string and metadata for validation
  const keyData = JSON.stringify({ 
    key: key.key,
    conversationId: conversationId
  });
  localStorage.setItem(`chat_key_${conversationId}`, keyData);
}

/**
 * Retrieve encryption key from localStorage
 * Since the key is deterministic, we regenerate it from patientId and doctorId
 * The stored key string is used for validation only
 */
export function getEncryptionKey(conversationId: number, patientId: number, doctorId: number | null | undefined, isSupport?: boolean): EncryptionKey | null {
  // Always regenerate the key since it's deterministic
  // This ensures consistency even if parameters change
  // Support: patientId:patientId (isSupport=true). Doctor chat: patientId:doctorId.
  const regeneratedKey = generateConversationKey(conversationId, patientId, doctorId, isSupport);
  
  // Debug logging (always enabled for troubleshooting)
  if (typeof console !== 'undefined' && console.log) {
    console.log(`[getEncryptionKey] conv=${conversationId}, patientId=${patientId}, doctorId=${doctorId}, isSupport=${isSupport}, key=${regeneratedKey.key.substring(0, 20)}...`);
  }
  
  return regeneratedKey;
}
